import React from 'react';
import { Box, Typography, Grid, useTheme } from '@mui/material';
import Card from './UIKit/Card';
import GradientProgress from './UIKit/GradientProgress';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import BarChartIcon from '@mui/icons-material/BarChart';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { useTranslation } from 'react-i18next';

const StatsCard = ({ stats }) => {
  const theme = useTheme();
  const { t } = useTranslation(['common', 'tests']);

  // Format stats for display - updated logic
  const formattedStats = {
    averageScore:
      stats.totalTests === 0
        ? t('common:stats.no_data')
        : `${Math.round(stats.averageScore || 0)}%`,
    bestScore:
      stats.totalTests === 0
        ? t('common:stats.no_data')
        : `${Math.round(stats.bestScore || 0)}%`,
    worstScore:
      stats.totalTests === 0
        ? t('common:stats.no_data')
        : `${Math.round(stats.worstScore || 0)}%`,
    quizCompletion:
      stats.totalTests === 0
        ? t('common:stats.no_data')
        : `${stats.uniqueCompletedTests || 0} / ${
            stats.totalCompletedTests || stats.totalTests || 'N/A'
          }`,
    totalTests: stats.totalTests || 0,
    uniqueCompletedTests: stats.uniqueCompletedTests || 0,
    totalAttempts: stats.totalAttempts || 0,
    passedTests: stats.passedTests || 0,
    failedTests: stats.failedTests || 0,
    progress: stats.progress || 0,
  };

  return (
    <Grid container spacing={2} sx={{ mb: 4 }}>
      {/* Общая статистика */}
      <Grid item xs={12} md={6}>
        <Card sx={{ height: '100%' }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: '600',
              mb: 3,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <BarChartIcon /> {t('common:stats.overall_statistics')}
          </Typography>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6}>
              <Box>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
                >
                  <EmojiEventsIcon fontSize="small" />
                  <Typography variant="body1">
                    {t('common:stats.average_score')}
                  </Typography>
                </Box>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 'bold', fontSize: '2rem' }}
                >
                  {formattedStats.averageScore}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
                >
                  <CheckCircleOutlineIcon fontSize="small" />
                  <Typography variant="body1">
                    {t('common:stats.total_tests')}
                  </Typography>
                </Box>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 'bold', fontSize: '2rem' }}
                >
                  {formattedStats.totalTests}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Box>
            <Typography variant="body1" sx={{ mb: 1 }}>
              {t('common:stats.overall_progress')}
            </Typography>
            <Box sx={{ position: 'relative' }}>
              <Typography
                variant="body2"
                sx={{
                  position: 'absolute',
                  right: 0,
                  top: -20,
                  fontWeight: 'bold',
                }}
              >
                {formattedStats.progress}%
              </Typography>
              <GradientProgress value={formattedStats.progress} height={8} />
            </Box>
          </Box>
        </Card>
      </Grid>

      {/* Детали тестов */}
      <Grid item xs={12} md={6}>
        <Card sx={{ height: '100%' }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: '600',
              mb: 3,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <AssignmentIcon /> {t('common:stats.quiz_details')}
          </Typography>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6}>
              <Box>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
                >
                  <EmojiEventsIcon
                    fontSize="small"
                    sx={{ color: theme.palette.success.main }}
                  />
                  <Typography variant="body1">
                    {t('common:stats.best_score')}
                  </Typography>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 'bold',
                    fontSize: '2rem',
                    color: theme.palette.success.main,
                  }}
                >
                  {formattedStats.bestScore}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
                >
                  <ErrorOutlineIcon
                    fontSize="small"
                    sx={{ color: theme.palette.error.main }}
                  />
                  <Typography variant="body1">
                    {t('common:stats.lowest_score')}
                  </Typography>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 'bold',
                    fontSize: '2rem',
                    color: theme.palette.error.main,
                  }}
                >
                  {formattedStats.worstScore}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Одна строка: завершено/всего тестов и попытки, объединено в грид */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6}>
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
              >
                <CheckCircleOutlineIcon fontSize="small" />
                <Typography variant="body1">
                  {t('common:stats.quiz_completion')}
                </Typography>
              </Box>
              <Typography
                variant="h4"
                sx={{ fontWeight: 'bold', fontSize: '2rem' }}
              >
                {formattedStats.quizCompletion}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
              >
                <AssignmentIcon fontSize="small" />
                <Typography variant="body1">
                  {t('common:stats.total_attempts')}
                </Typography>
              </Box>
              <Typography
                variant="h4"
                sx={{ fontWeight: 'bold', fontSize: '2rem' }}
              >
                {formattedStats.totalAttempts}
              </Typography>
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={6}>
              <Box>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
                >
                  <CheckCircleOutlineIcon
                    fontSize="small"
                    sx={{ color: theme.palette.success.main }}
                  />
                  <Typography variant="body1">
                    {t('common:stats.passed_tests')}
                  </Typography>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 'bold',
                    fontSize: '1.75rem',
                    color: theme.palette.success.main,
                  }}
                >
                  {formattedStats.passedTests}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
                >
                  <ErrorOutlineIcon
                    fontSize="small"
                    sx={{ color: theme.palette.error.main }}
                  />
                  <Typography variant="body1">
                    {t('common:stats.failed_tests')}
                  </Typography>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 'bold',
                    fontSize: '1.75rem',
                    color: theme.palette.error.main,
                  }}
                >
                  {formattedStats.failedTests}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Card>
      </Grid>
    </Grid>
  );
};

export default StatsCard;
